import { Box, SxProps, useMediaQuery, useTheme } from '@mui/material'
import React, { useMemo } from 'react'
import useChartTraces from 'src/Hooks/use-chart-traces'
import PieChart from 'src/Libs/chart-kit/pie-charts/components/PieChart'
import { useChartDataAndCells } from 'src/Libs/chart-kit/pie-charts/hooks'
import { DEFAULT_PIE_CHART_HEIGHT } from 'src/Libs/system-charts/default-charts/pie-chart-constants'
import { getInitialHouseConsumptionPieChart } from 'src/Libs/system-charts/default-charts/utils/initial-chart-config'
import { useAutarkyChartValues } from 'src/Libs/live-view'
import { isFlowTheme } from 'src/Libs/utils'
import { Ai360LabelIcon } from './Ai360LabelIcon'
import useDynamicPricesSettings from 'src/Libs/dynamic-power-prices/hooks/use-dynamic-prices-settings'

export interface Props {
  storageId: string | undefined
  isDynamicPowerPriceEnabled?: boolean
  sx?: SxProps
  isInactive?: boolean
  textColor?: string
  showAI360V2Animation?: boolean
}

const GuiPieChart: React.FC<Props> = ({ storageId, sx, isDynamicPowerPriceEnabled = false, isInactive, textColor, showAI360V2Animation = false }) => {
  const { getTraceInfoFromName } = useChartTraces()
  const upSm = useMediaQuery(useTheme().breakpoints.up('sm'))
  const { dynamicPricesSettings } = useDynamicPricesSettings({ storageId: storageId || '' })
  const isDynamicEmsEnabled = dynamicPricesSettings?.isDynamicEmsEnabledForBattery || false

  const labelNameKey = getThemedLabelNameKey(isDynamicPowerPriceEnabled)
  const showFootnoteAsterix = isDynamicPowerPriceEnabled && !isFlowTheme
  const chartHeight = upSm ? DEFAULT_PIE_CHART_HEIGHT : 110

  const rechartData = useAutarkyChartValues(storageId)

  const initialChartLayout = useMemo(
    () => getInitialHouseConsumptionPieChart(chartHeight, isDynamicPowerPriceEnabled),
    [isDynamicPowerPriceEnabled, chartHeight]
  )
  const traceNames = initialChartLayout?.traces.reduce((names: Record<string, string>, currentTrace) => {
    const { name } = getTraceInfoFromName(currentTrace.key)

    if (names[currentTrace.key]) {
      names[currentTrace.key] = name
    }

    return { ...names, [currentTrace.key]: name }
  }, {})

  const { data, cells } = useChartDataAndCells({
    rechartData,
    customTraces: initialChartLayout?.traces,
    impossibleTraces: [],
    traceNames,
    view: 'day',
    isDynamicPowerPriceEnabled,
    isCostSavingsPieChart: false,
    isLiveView: true,
    inactiveView: isInactive,
  })

  return (
    <Box sx={sx}>
      <PieChart
        data={data}
        cells={cells}
        traceNames={traceNames}
        labelTraces={initialChartLayout.labelTraces}
        labelNameKey={labelNameKey}
        height={chartHeight}
        /* don't show icon if AI360 V2 animation is active */
        icon={!showAI360V2Animation && isDynamicEmsEnabled ? <Ai360LabelIcon width="42%" height="40%" /> : undefined}
        showFootnoteAsterix={showFootnoteAsterix}
        isInactive={isInactive}
        textColor={textColor}
      />
    </Box>
  )
}

export default GuiPieChart

const getThemedLabelNameKey = (isDynamicPowerPriceEnabled: boolean): string | undefined => {
  if (isFlowTheme) return 'live-values.pie-chart.home-consumption.label'
  return isDynamicPowerPriceEnabled ? 'chart.pie-chart.home-consumption.label' : 'live-values.pie-chart.home-consumption.label'
}
